/**
 * DomainMunicipioContext
 * ======================
 * Context global que provê o estado do "domínio customizado" para toda a árvore.
 * Quando `isCustomDomain=true`:
 *   - injeta header `X-Municipio-Domain: <hostname>` em todas as chamadas axios
 *   - permite que componentes (Header, Home, SEOHead) pré-filtrem por município
 *
 * NÃO interfere no domínio principal (empreendecomunidade.com.br):
 *   - `isCustomDomain=false` significa que a árvore segue o fluxo legado
 *     (seleção manual de cidade).
 */
import React, { createContext, useContext, useEffect } from 'react';
import axios from 'axios';
import { useDomainContext } from '../hooks/useDomainContext';
import { setTenantSlug } from '../services/tenantSlugRef';

const DomainMunicipioContext = createContext({
  isCustomDomain: false,
  municipio: null,
  loading: true,
  resolved: false,
});

const HEADER_NAME = 'X-Municipio-Domain';

export function DomainMunicipioProvider({ children }) {
  const ctx = useDomainContext();

  // Configura o header de domínio na instância default do axios.
  // Cobre todas as chamadas que usam o axios global. Para fetch() puro,
  // os componentes podem ler o context e adicionar manualmente.
  useEffect(() => {
    if (ctx.loading) return;
    const hostname = (typeof window !== 'undefined'
      ? window.location.hostname
      : '');
    if (ctx.isCustomDomain && hostname) {
      axios.defaults.headers.common[HEADER_NAME] = hostname;
    } else {
      delete axios.defaults.headers.common[HEADER_NAME];
    }
  }, [ctx.isCustomDomain, ctx.loading]);

  // Multi-tenant guard: quando em domínio customizado, força o slug do
  // município como default GLOBAL. Sem isso, páginas sem prefixo (/vagas,
  // /campanhas, /social, /coletivos, /noticias, /feed) enviam requests
  // SEM `municipio_slug` e o backend pode servir dados globais/mocks.
  // Isso complementa a resolução via URL (TenantSlugSync inside TenantRoutes).
  useEffect(() => {
    if (ctx.loading || !ctx.resolved) return;
    if (ctx.isCustomDomain && ctx.municipio?.slug) {
      setTenantSlug(ctx.municipio.slug);
    }
    // NÃO limpamos aqui: se o usuário navega para uma rota tenant explícita
    // (/:municipioSlug/*), o TenantSlugSync sobrescreve com o slug da URL.
  }, [ctx.isCustomDomain, ctx.municipio, ctx.loading, ctx.resolved]);

  // Atualiza o <title> com o nome do município (UX/SEO baseline).
  useEffect(() => {
    if (!ctx.resolved || !ctx.isCustomDomain || !ctx.municipio) return;
    const nome = ctx.municipio.nome;
    if (nome) {
      document.title = `${nome} — Empreende Comunidade`;
    }
  }, [ctx.resolved, ctx.isCustomDomain, ctx.municipio]);

  return (
    <DomainMunicipioContext.Provider value={ctx}>
      {children}
    </DomainMunicipioContext.Provider>
  );
}

export function useDomainMunicipio() {
  return useContext(DomainMunicipioContext);
}

export default DomainMunicipioContext;
